import { useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

const SESSION_STORAGE_KEY = 'nyris-api-key';
export const API_KEY_CHANGED_EVENT = 'nyris-api-key-changed';

const readStoredApiKey = (): string => {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

const persistApiKey = (apiKey: string): void => {
  try {
    if (apiKey) {
      sessionStorage.setItem(SESSION_STORAGE_KEY, apiKey);
    } else {
      sessionStorage.removeItem(SESSION_STORAGE_KEY);
    }
  } catch {
    // sessionStorage may be unavailable (e.g. private mode); ignore.
  }
};

export const hasNyrisApiKey = (): boolean => {
  const fromSettings = window.settings?.apiKey?.trim();
  if (fromSettings) return true;
  return Boolean(readStoredApiKey().trim());
};

interface ApiKeyInputProps {
  className?: string;
  inputClassName?: string;
  autoFocus?: boolean;
  hideLabel?: boolean;
}

// Lets a developer paste a nyris API key at runtime so searches use it.
// The key is kept in sessionStorage and mirrored onto window.settings.apiKey,
// which every search reads when constructing the NyrisAPI client.
export function ApiKeyInput({
  className,
  inputClassName,
  autoFocus = false,
  hideLabel = false,
}: ApiKeyInputProps): JSX.Element {
  const [apiKey, setApiKey] = useState<string>(
    () => readStoredApiKey() || window.settings.apiKey || '',
  );

  // Keep window.settings.apiKey (read by every search) and sessionStorage in
  // sync with the field. Runs on mount so a stored key is applied immediately.
  useEffect(() => {
    window.settings.apiKey = apiKey;
    persistApiKey(apiKey);
    window.dispatchEvent(
      new CustomEvent(API_KEY_CHANGED_EVENT, { detail: apiKey }),
    );
  }, [apiKey]);

  // Stay in sync if another ApiKeyInput instance updates the key.
  useEffect(() => {
    const onKeyChanged = (event: Event) => {
      const next = (event as CustomEvent<string>).detail ?? '';
      setApiKey(current => (current === next ? current : next));
    };
    window.addEventListener(API_KEY_CHANGED_EVENT, onKeyChanged);
    return () =>
      window.removeEventListener(API_KEY_CHANGED_EVENT, onKeyChanged);
  }, []);

  return (
    <label
      className={twMerge([
        'flex items-center gap-2 text-[12px] text-[#2B2C46]',
        className,
      ])}
    >
      {!hideLabel && (
        <span className="whitespace-nowrap font-medium">API key</span>
      )}
      <input
        type="password"
        value={apiKey}
        placeholder="nyris API key"
        aria-label="API key"
        autoComplete="off"
        spellCheck={false}
        autoFocus={autoFocus}
        onChange={event => setApiKey(event.target.value.trim())}
        className={twMerge([
          'w-[180px] desktop:w-[220px]',
          'h-8 px-2',
          'rounded border border-[#E0E0E0]',
          'bg-white text-[12px]',
          'outline-none focus:border-primary',
          inputClassName,
        ])}
      />
    </label>
  );
}
